import React, { useMemo, useState } from 'react';
import { FlaskConical, HelpCircle } from 'lucide-react';
import { WorldSection } from './WorldPageHeader';
import { SourceCard } from './SourceCard';
import { BagSourceList } from './BagSourceList';
import { DataStatus, missingFiles } from './DataStatus';
import { groupLabel, playerItemSources, qualityLabel, routeQuality, sourceGroup } from '../../utils/itemSources';
import type { Quality, ResolvedSource, SourceGroup } from '../../utils/itemSources';
import { isOpenableBag, playerBagSources } from '../../utils/bagSources';
import type { BagSource } from '../../utils/bagSources';
import { recipesProducing } from '../../utils/recipeLinks';
import type { WorldData } from '../../types/world';
const GROUPS: SourceGroup[] = ['drop', 'event', 'box', 'quest', 'other'];
const QUALITIES: Quality[] = ['normal', 'excellent', 'ancient', 'other'];

/**
 * "Dónde conseguirlo": todas las formas de obtención documentadas, evaluadas ruta por ruta y
 * separadas por tipo (drop, invasión/evento, caja, misión) y calidad (normal, Excellent, Ancient).
 * Las asociaciones retenidas por conflicto no aparecen; las rutas desactivadas van al final.
 */
export function WhereToGet({
  w,
  id,
  name
}: {
  w: WorldData;
  id: string;
  name: string;
}) {
  const all = useMemo(() => playerItemSources(w, id), [w, id]);
  const active = all.filter(s => s.status !== 'disabled');
  const disabled = all.filter(s => s.status === 'disabled');
  const bags = useMemo(() => {
    const viaRoutes = new Set(active.map(s => s.route.bagId).filter(Boolean));
    return playerBagSources(w, id).filter(b => !viaRoutes.has(b.bag.id));
  }, [w, id, active]);
  const crafted = useMemo(() => recipesProducing(id, name), [id, name]);
  const [showDisabled, setShowDisabled] = useState(false);
  const missingRoutes = missingFiles(w, ['routes', 'routeTargets']);
  const missingBags = missingFiles(w, ['bags', 'bagTriggers', 'bagRewards']);
  const grouped = GROUPS.map(g => ({
    g,
    sources: active.filter(s => sourceGroup(s) === g),
    bags: g === 'box' ? bags.filter(isOpenableBag) : g === 'event' ? bags.filter(b => !isOpenableBag(b)) : [] as BagSource[]
  })).filter(x => x.sources.length || x.bags.length);
  const located = active.some(s => s.eventMaps.length || s.eventMapId !== undefined || s.targets.some(t => t.spawns.length));
  const documented = active.some(s => !s.pending) || bags.length > 0;
  const total = active.length + bags.length;
  return <WorldSection id="donde-conseguirlo" title="Dónde conseguirlo" count={total + crafted.length} data-id="element-1786" data-mp="2qwdkzantaw89">
      <div className="-mt-1 mb-3 flex flex-wrap gap-1.5" data-id="element-1787" data-mp="2uxfmxz945mdx">
        {missingRoutes.length ? <DataStatus kind="unchecked" data-id="element-1788" data-mp="2zws9eiw2orfz" /> : <>
            {documented && <DataStatus kind="route" data-id="element-1789" data-mp="34qwfz11fexgj" />}
            {located && <DataStatus kind="location" withDate={false} data-id="element-1790" data-mp="27ar1j3kwbbsk" />}
            {active.some(s => s.pending) && <DataStatus kind="partial" data-id="element-1791" data-mp="2c8siniyrh11e" />}
          </>}
      </div>

      {missingRoutes.length > 0 && <p className="mb-3 flex items-start gap-2 rounded-xl bg-white/[0.03] px-3 py-2.5 text-sm text-gray-300" data-id="element-1792" data-mp="24q1f9e2pyuxg">
          <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1793" data-mp="1zrzvss1dstlg" />
          No comprobado: no se pudieron cargar las rutas de obtención. Esto no significa que el ítem no tenga drops.
        </p>}

      {!missingRoutes.length && total === 0 && crafted.length === 0 && <p className="text-gray-400" data-id="element-1794" data-mp="1lrp3dkkfb6y6">{disabled.length ? 'Ninguna forma de obtención está disponible en la configuración actual.' : 'Ubicación de obtención pendiente de confirmar.'}</p>}

      <div className="space-y-6" data-id="element-1795" data-mp="1c25h04xi4uyk">
        {grouped.map(({
        g,
        sources,
        bags: gb
      }) => {
        const qs = QUALITIES.filter(q => sources.some(s => routeQuality(s.route) === q));
        return <div key={g} data-id="element-1796" data-mp="12z4gcjrenw2a">
              <h3 className="mb-2.5 font-cinzel text-sm md:text-base font-bold text-[#D4AF37]" data-id="element-1797" data-mp="132ifzk8opr61">{groupLabel[g]}</h3>
              {qs.map(q => <div key={q} className="mb-3 last:mb-0" data-id="element-1798" data-mp="2y3c5bgxctp74">
                  {(qs.length > 1 || q !== 'normal') && <p className="mb-2 text-xs font-semibold text-gray-300" data-id="element-1799" data-mp="325fjwefbl9wm">{qualityLabel[q]}</p>}
                  <ul className="grid gap-3" data-id="element-1800" data-mp="2oeat81txjryf">
                    {sources.filter(s => routeQuality(s.route) === q).map(s => <SourceCard key={s.key} w={w} s={s} data-id="element-1801" data-mp="2tc2lwy2efi3o" />)}
                  </ul>
                </div>)}
              {gb.length > 0 && <div className={sources.length ? 'mt-3' : ''} data-id="element-1802" data-mp="2ehxyd0mnf823">
                  {missingBags.length > 0 ? <DataStatus kind="unchecked" data-id="element-1803" data-mp="2jclfufj8j276" /> : <BagSourceList w={w} sources={gb} data-id="element-1804" data-mp="25f6np31lkgbi" />}
                </div>}
            </div>;
      })}

        {crafted.length > 0 && <div data-id="element-1805" data-mp="2ag4357v82w9b">
            <h3 className="mb-2.5 font-cinzel text-sm md:text-base font-bold text-[#D4AF37]" data-id="element-1806" data-mp="3nk1rfwkbblw">Fabricación</h3>
            {total === 0 && <p className="mb-2 text-sm text-gray-300" data-id="element-1807" data-mp="8n6cniox4frj">No tiene drops documentados: se obtiene combinando materiales.</p>}
            <ul className="grid gap-2" data-id="element-1808" data-mp="ird2wpl9lrq6">
              {crafted.map(r => <li key={r.id} data-id="element-1809" data-mp="ds9wb0pmzdg3">
                  <a href="#como-crearlo" className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A1628] p-3.5 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-1810" data-mp="dt1umjambpa2">
                    <FlaskConical className="h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-1811" data-mp="mrdiidk3o8yt" />
                    <span className="min-w-0" data-id="element-1812" data-mp="11frqf2wdinm8">
                      <span className="block font-semibold text-white" data-id="element-1813" data-mp="whymyhmgg7pn">{r.title} · {r.machine ?? 'Chaos Machine'}</span>
                      <span className="block text-xs text-gray-400" data-id="element-1814" data-mp="1ai9fdp3exucx">Ver materiales y pasos más abajo · receta clásica, pendiente de validar en Vanguard</span>
                    </span>
                  </a>
                </li>)}
            </ul>
          </div>}

        {disabled.length > 0 && <div data-id="element-1815" data-mp="388kgrl4fl417">
            <button type="button" onClick={() => setShowDisabled(v => !v)} aria-expanded={showDisabled} className="h-10 text-sm font-semibold text-gray-400 hover:text-white" data-id="element-1816" data-mp="3893o67wgga0z">
              {showDisabled ? 'Ocultar' : 'Ver'} formas no disponibles ahora ({disabled.length})
            </button>
            {showDisabled && <ul className="mt-2 grid gap-3" data-id="element-1817" data-mp="1l3rfk6e9zjj3">{disabled.map((s: ResolvedSource) => <SourceCard key={s.key} w={w} s={s} data-id="element-1818" data-mp="1q56ynsww7fyu" />)}</ul>}
          </div>}
      </div>
    </WorldSection>;
}